import { navigate } from '@reach/router';

import humps from 'humps';
import moment from 'moment';

import { HIDECOOKIESBANNERCOOKIE } from './components/Header/AdminHeader/Components/CookiesBanner/CookiesBanner';
import { paramsToQueryString } from './contrib/Utils/paramsToQueryString';
import { APP_ENV_SUBDOMAIN } from './contrib/Utils/APP_ENV_SUBDOMAIN';

export const API_BASE = `https://${APP_ENV_SUBDOMAIN}.wagl.golf/api`;

const today = moment()
  .startOf('day')
  .subtract(1, 'days')
  .format();

const api = {
  admin: {
    addons: (clubId: number, eventId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/addons/`,
    allRoundFormats: (clubId: number, eventId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/formats`,
    clubCard: (clubId: number) => `${API_BASE}/staff/clubs/${clubId}/card/`,
    clubInvitations: (clubId: number | string) => `${API_BASE}/invitations/?club=${clubId}`,
    clubManagement: {
      get: (clubId: number) => `${API_BASE}/staff/clubs/${clubId}/managements/`,
      update: (clubId: number, managementId: number) =>
        `${API_BASE}/staff/clubs/${clubId}/managements/${managementId}`
    },
    clubTransactionRefunds: (clubId: number, transactionId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/transactions/${transactionId}/refund/`,
    clubTransactions: (clubId: number) => `${API_BASE}/staff/clubs/${clubId}/transactions/`,
    clubs: `${API_BASE}/staff/clubs/`,
    coupons: (clubId: number, eventId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/coupons/`,
    courses: () => `${API_BASE}/staff/courses/`,
    detailGames: (clubId: number, eventId: number, roundId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/detail-games/`,
    eventAward: (clubId: number, eventId: number, awardId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/awards/${awardId}`,
    eventAwards: (clubId: number, eventId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/awards/`,
    eventEnrollments: (clubId: number, eventId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/enrollments/`,
    eventFormat: (clubId: number, eventId: number, formatId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/formats/${formatId}`,
    eventFormatAwards: (clubId: number, eventId: number, formatId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/formats/${formatId}/awards`,
    eventFormats: (clubId: number, eventId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/formats`,
    eventInvitations: (eventId: number) => `${API_BASE}/invitations/?event=${eventId}`,
    eventLeaderboards: (clubId: number, eventId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/leaderboards`,
    eventResults: (params?: { [name: string]: number | string }) =>
      `${API_BASE}/staff/results/events/${paramsToQueryString(params)}`,
    eventRounds: (clubId: number, eventId: number, roundId?: number) =>
      roundId
        ? `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/`
        : `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/`,
    eventTeam: (clubId: number, eventId: number, teamId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/teams/${teamId}`,
    eventTeams: (clubId: number, eventId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/teams`,
    events: (clubId: number) => `${API_BASE}/staff/clubs/${clubId}/events/`,
    flights: (clubId: number, eventId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/flights/`,
    formats: `${API_BASE}/staff/formats/`,
    games: (clubId: string, eventId: string, roundId: string, gameId?: string) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/games/${
        gameId ? gameId : ''
      }`,
    group: (
      clubId: string | number,
      eventId: string | number,
      roundId: string | number,
      groupId: string | number
    ) => `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/groups/${groupId}`,
    invitations: `${API_BASE}/invitations/`,
    notifications: `${API_BASE}/staff/notifications/`,
    pages: (clubId: number) => `${API_BASE}/staff/clubs/${clubId}/pages/`,
    prices: (clubId: number, eventId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/prices/`,
    roundAward: (clubId: number, eventId: number, roundId: number, awardId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/awards/${awardId}`,
    roundAwards: (clubId: number, eventId: number, roundId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/awards`,
    roundFormatAwards: (leaderboardId: number) =>
      `${API_BASE}/staff/leaderboards/${leaderboardId}/awards`,
    roundFormats: (clubId: number, eventId: number, roundId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/formats`,
    roundGame: (clubId: number, eventId: number, roundId: number, enrollentId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/games/${enrollentId}/`,
    roundLeaderboard: (leaderboardId: number) => `${API_BASE}/staff/leaderboards/${leaderboardId}`,
    roundLeaderboards: (clubId: number, eventId: number, roundId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/leaderboards`,
    roundShotgun: (clubId: number | string, eventId: number | string, roundId: number | string) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/shotgun`,
    season: (clubId: number, seasonId: number) =>
      `${API_BASE}/staff/clubs/${clubId}/seasons/${seasonId}`,
    seasons: (clubId: number) => `${API_BASE}/staff/clubs/${clubId}/seasons/`,
    teamGames: (clubId: string, eventId: string, roundId: string, teamGameId?: string) =>
      `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/team-games/${
        teamGameId ? teamGameId : ''
      }`
  },
  clubEvents: (clubId: string) => `${API_BASE}/player/events/?club=${clubId}`,
  clubs: `${API_BASE}/player/clubs/`,
  eventRegistration: `${API_BASE}/player/enrollments/`,
  events: `${API_BASE}/player/events/`,

  futureClubEvents: (clubId: string) =>
    `${API_BASE}/player/events/?start__gte=${today}&club=${clubId}`,
  futureClubGames: (clubId: string) =>
    `${API_BASE}/player/games/?round__start__gte=${today}&enrollment__event__club=${clubId}`,
  futureEvents: `${API_BASE}/player/events/?start__gte=${today}`,
  futureGames: `${API_BASE}/player/games/?round__start__gte=${today}`,
  games: `${API_BASE}/player/games/`,
  group: (groupId: number) => `${API_BASE}/player/groups/${groupId}/`,
  groupGame: (groupId: number, gameId: number) =>
    `${API_BASE}/player/groups/${groupId}/games/${gameId}`,
  groupGames: (groupId: number) => `${API_BASE}/player/groups/${groupId}/games/`,
  invitations: `${API_BASE}/invitations/`,
  login: `${API_BASE}/rest-auth/login/`,
  logout: `${API_BASE}/rest-auth/logout/`,
  memberships: `${API_BASE}/player/memberships/`,
  pages: (clubId: number) => `${API_BASE}/player/clubs/${clubId}/pages/`,
  payments: `${API_BASE}/payment-methods/`,

  player_profile: `${API_BASE}/player/users/`,
  profile: `${API_BASE}/profile/`,
  refreshToken: `${API_BASE}/api-token-refresh/`,
  register: `${API_BASE}/rest-auth/registration/`,
  requestResetPassword: `${API_BASE}/rest-auth/password/reset/`,
  resetPassword: `${API_BASE}/rest-auth/password/reset/confirm`,
  results: (
    scope: 'staff' | 'player',
    elements: 'rounds' | 'holes' | 'events',
    params?: { [name: string]: number | string }
  ) => `${API_BASE}/${scope}/results/${elements}/${paramsToQueryString(params)}`,
  roundTee: (clubId: number, eventId: number, roundId: number, roundTeeId: number) =>
    `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/tees/${roundTeeId}`,
  roundTees: (clubId: number, eventId: number, roundId: number) =>
    `${API_BASE}/staff/clubs/${clubId}/events/${eventId}/rounds/${roundId}/tees/`,
  seasonAwards: (section: 'staff' | 'player', seasonId: number) =>
    `${API_BASE}/${section}/results/awards/?season_id=${seasonId}`,
  seasons: (clubId: number) => `${API_BASE}/player/clubs/${clubId}/seasons/`,
  standings: `${API_BASE}/player/results/`,
  teamRoundHoleLeaderboardResults: (roundId: number, player?: boolean) =>
    `${API_BASE}/${player ? 'player' : 'staff'}/results/teams/holes/?round_id=${roundId}`,
  teamRoundLeaderboardResults: (
    scope: 'staff' | 'player',
    elements: 'round' | 'event',
    params?: { [elementId: string]: number }
  ) => `${API_BASE}/${scope}/results/teams/${elements}s/${paramsToQueryString(params)}`,
  user: `${API_BASE}/rest-auth/user/`
};

export default api;

enum RequestTypes {
  DELETE = 'DELETE',
  GET = 'GET',
  MULTIPART = 'MULTIPART',
  PUT = 'PUT',
  PATCH = 'PATCH',
  POST = 'POST'
}

const getHeaders = (method: RequestTypes) => {
  const headers: any = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  };

  const { token } = JSON.parse(localStorage.getItem('auth') || '{}');
  if (token) {
    headers.Authorization = `JWT ${token}`;
  }
  if (method === RequestTypes.MULTIPART) {
    delete headers['Content-Type']; // important, form data will not be sent without deleting this
  }

  return headers;
};

const fetchHelper = async (url: string, method: RequestTypes, data?: any) => {
  if (url.indexOf('?') < 0) {
    url = url.endsWith('/') ? url : `${url}/`;
  }

  const attributes: any = {
    headers: getHeaders(method),
    method: method === RequestTypes.MULTIPART ? RequestTypes.PATCH : method
  };

  if (data) {
    attributes.body =
      method === RequestTypes.MULTIPART ? data : JSON.stringify(humps.decamelizeKeys(data));
  }

  try {
    const response = await fetch(url, attributes);

    if (response.ok) {
      return method !== RequestTypes.DELETE ? humps.camelizeKeys(await response.json()) : {};
    } else {
      const body = await response.json();
      const message =
        Object.entries(body).length > 0 ? Object.entries(body)[0][1] : response.statusText;

      if (response.status === 401) {
        localStorage.removeItem('auth');
        localStorage.removeItem(HIDECOOKIESBANNERCOOKIE);

        navigate('/login');

        return {
          error: {
            body: 'Token has expired',
            status: response.status,
            statusText: ['Token has expired']
          }
        };
      }

      if (response.status === 404 && !url.includes(api.invitations)) {
        navigate('/404');
      }

      return { error: { status: response.status, statusText: message, body } };
    }
  } catch (e) {
    return { error: { statusText: e } };
  }
};

interface IRequest {
  delete: (url: string) => Promise<any>;
  get: (url: string) => Promise<any>;
  multipart: (url: string, body: any) => Promise<any>;
  patch: (url: string, data: any) => Promise<any>;
  post: (url: string, data: any) => Promise<any>;
  put: (url: string, data: any) => Promise<any>;
}

export const request: IRequest = {
  delete: (url: string) => {
    return fetchHelper(url, RequestTypes.DELETE);
  },
  get: (url: string) => {
    return fetchHelper(url, RequestTypes.GET);
  },
  multipart: (url: string, data: any) => {
    return fetchHelper(url, RequestTypes.MULTIPART, data);
  },
  patch: (url: string, data: any) => {
    return fetchHelper(url, RequestTypes.PATCH, data);
  },
  post: (url: string, data: any) => {
    return fetchHelper(url, RequestTypes.POST, data);
  },
  put: (url: string, data: any) => {
    return fetchHelper(url, RequestTypes.PUT, data);
  }
};
